import { toHTML } from "@portabletext/to-html";
import type { Product } from "@bluebottlecoffee/design-system/components/lib/types";
import { ConversionProps } from "@bluebottlecoffee/design-system/components";
import type { PrepayProps } from "@bluebottlecoffee/design-system/components";
import { getProductIconName } from "../utils/get-product-icon-name";
import { DereferencedShopCard } from "../sanity/shop-card-queries";
import { toImageProps, toImageResponsiveProps } from "./image";
import { Product as ProductSchema } from "../sanity-schema";
import { toVariantProps } from "./variant";
import {
  DereferencedOptionType,
  DereferencedSubscriptionOption,
} from "../sanity/product-queries";
import { DereferencedVariant } from "../sanity/variant-queries";

export type ConversionSchema = Omit<
  ProductSchema,
  | "crossSellProducts"
  | "optionTypes"
  | "subscription"
  | "subscriptionOptions"
  | "variants"
  | "shopcard"
  | "bundle"
> & {
  crossSellProducts?: ConversionSchema[];
  optionTypes: DereferencedOptionType[];
  primaryShopCollectionName?: string;
  subscription: Product["subscription"];
  subscriptionOptions: DereferencedSubscriptionOption[];
  variants: DereferencedVariant[];
  shopCard?: DereferencedShopCard;
  bundle?: {
    enabledBundle: boolean;
    variantOne: DereferencedVariant;
    variantTwo: DereferencedVariant;
    bundleVariant: DereferencedVariant;
  };
};

export type ProductWithShortDescription = Product & {
  shortDescription: string;
};

export function toProductProps(
  {
    slug,
    name,
    subscription,
    subscriptionOptions,
    variants,
    images,
    subscriptionType,
    isGiftCard,
    soldOutCtas,
    optionTypes,
    quickSubscribeCard,
    roastLevel,
    descriptionV2,
    flavorProfile,
    processing,
  }: ConversionSchema,
  lang: string,
): ProductWithShortDescription {
  const shortDescription = quickSubscribeCard?.product?.description
    ? toHTML(quickSubscribeCard.product.description[lang])
    : toHTML(descriptionV2?.[0].description[lang]);

  return {
    images: images.map((i) => toImageResponsiveProps(i, lang)),
    name: name[lang],
    shortDescription,
    slug: slug.current,
    subscription,
    subscriptionOptions:
      subscriptionOptions?.map((subscriptionOption) => {
        // subscriptionOptions are Sanity objects, which do not have _id's :(
        // so mapping _key to component expected type _id
        // rather than refactoring the world rn
        const { _key, quantityLabel, ...rest } = subscriptionOption;

        // quantityLabel.label comes through collection and shop pages from Algolia as an object
        // whereas on the PDP, it is a string since it is localized in the Sanity query
        const label =
          quantityLabel.label && typeof quantityLabel.label === "object"
            ? quantityLabel.label![lang]
            : quantityLabel.label;

        return {
          ...rest,
          quantityLabel: {
            ...quantityLabel,
            ...(quantityLabel.imageType && {
              icon:
                quantityLabel.imageType === "image"
                  ? toImageProps(quantityLabel.image, lang)
                  : getProductIconName(
                      quantityLabel.imageType,
                      quantityLabel.quantity,
                    ),
            }),
            label,
          },
          _id: _key,
        };
      }) || null,
    subscriptionType: subscriptionType || null,
    variants: variants
      .filter((v) => v.forSale)
      .map((v) => toVariantProps(v, lang)),
    isGiftCard: isGiftCard || null,
    soldOutCtas: soldOutCtas
      ? soldOutCtas.map((soldOutCta) => {
          const { text, internal, isAnchorLink, external, blank } = soldOutCta;
          return {
            blank,
            isAnchorLink,
            text: text[lang],
            url: internal || external,
          };
        })
      : null,
    optionTypes: optionTypes.map((optionType) => ({
      presentation: optionType.presentation ?? null,
    })),
    // PDP V2
    flavors: flavorProfile?.[lang] || null,
    roast: roastLevel?.[0]
      ? {
          label: roastLevel[0].label[lang],
          roastLevel: roastLevel[0].roastLevel,
        }
      : null,
    descriptionV2: descriptionV2?.[0]
      ? {
          description: toHTML(descriptionV2[0].description[lang]),
          heading: descriptionV2[0].heading?.[lang] || null,
          icons:
            descriptionV2[0].details?.map((detail) => ({
              heading: detail.label?.[lang],
              description: detail.value?.[lang],
              image: toImageProps(detail.icon, lang),
            })) || null,
        }
      : null,
    processing: processing?.[0]
      ? {
          label: processing[0].label[lang],
          description: processing[0].description[lang],
        }
      : null,
  };
}

export function toConversionProps(
  data: ConversionSchema,
  lang: string,
): Omit<
  ConversionProps,
  | "addToCart"
  | "addToCartProps"
  | "copy"
  | "createStockRequest"
  | "subscribeProduct"
  | "useVariantAvailability"
  | "modifyGiftCards"
  | "emailIsValid"
  | "giftCardFormCopy"
  | "readMoreTriggerAriaLabel"
  | "readMoreTriggerLabel"
  | "shippingCopy"
  | "useVariantPrice"
> {
  return {
    product: toProductProps(data, lang),
  };
}

export interface PrepaidGift {
  enabled: boolean;
  offeringType?: "gift" | "discount";
  product?: {
    name: string;
    image: ProductSchema["images"][0];
  };
  prepayShipmentsLabel: string;
  numberOfInstallments: number;
  label?: string;
  discountLabel?: string;
  additionalDiscountLabel?: string;
  discountDescription?: string;
}

export const toPrepayProps = (
  data: PrepaidGift,
  lang: string,
): PrepayProps | undefined => {
  if (!data?.enabled) {
    return undefined;
  }

  const offeringType = data.offeringType ?? "gift";

  if (offeringType === "discount") {
    return {
      offeringType: "discount",
      prepayShipmentsLabel: data.prepayShipmentsLabel,
      numberOfInstallments: data.numberOfInstallments,
      label: data.discountLabel,
      additionalDiscountLabel: data.additionalDiscountLabel,
      discountDescription: data.discountDescription,
    };
  }

  if (!data.product) {
    return undefined;
  }

  return {
    offeringType: "gift",
    productName: data.product.name,
    image: toImageProps(data.product.image, lang),
    prepayShipmentsLabel: data.prepayShipmentsLabel,
    label: data.label,
    numberOfInstallments: data.numberOfInstallments,
  };
};
